import { AvatarDropdown, CallbackAlertBanner, LiveEventFeed } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/api';
import { ThunderboltOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React, { useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
import dayjs from 'dayjs';
import enUS from 'antd/locale/en_US';
import { Button, Tooltip, Badge } from 'antd';

const loginPath = '/user/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // If it is not a login page, search for current information
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  const [eventFeedVisible, setEventFeedVisible] = useState(false);

  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: initialState?.currentUser?.name,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    actionsRender: (props) => {
      if (props.isMobile) return [];
      return [
        <Tooltip key="pulse" title="Live System Pulse">
          <Button 
            type="text" 
            icon={<ThunderboltOutlined style={{ color: '#52c41a' }} />} 
            onClick={() => setEventFeedVisible(true)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%' 
            }}
          />
        </Tooltip>,
      ];
    },
    childrenRender: (children) => {
      return (
        <>
          <CallbackAlertBanner />
          {children}
          <LiveEventFeed 
            open={eventFeedVisible} 
            onClose={() => setEventFeedVisible(false)} 
          />
        </>
      );
    },
    onPageChange: () => {
      const { location } = history;
      const token = localStorage.getItem('token');
      if (!initialState?.currentUser && !token && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    menuHeaderRender: undefined,
    ...initialState?.settings,
  };
};

/**
 * Ant Design runtime configuration
 * @see https://umijs.org/docs/max/antd
 */
export const antd = {
  locale: enUS,
};

/**
 * Locale runtime configuration
 * @see https://umijs.org/docs/max/i18n
 */
export const locale = {
  default: 'en-US',
  baseSeparator: '-',
};

/**
 * Request runtime configuration
 * @see https://umijs.org/docs/max/request
 */
export const request: RequestConfig = {
  ...errorConfig,
  requestInterceptors: [
    (config: any) => {
      // Add auth token to headers
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = {
          ...config.headers,
          'X-Admin-Token': token,
        };
      }
      return config;
    },
  ],
};

